import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Flex, Title } from "@mantine/core";
import { Campaign, CampaignState } from "src/graphql/generated/graphql";
import { RoutePath } from "src/routes";

import { CampaignsDataGrid } from "../../components/CampaignsDataGrid";
import { Pagination } from "../../components/Pagination";
import { useAuthContext } from "../../hooks/auth";
import { useCampaigns } from "../../hooks/campaigns";
import { useSearchInput } from "../../hooks/searchInput";

export interface CampaignsPageSearchParams extends Record<string, string | string[]> {
    search?: string;
    tenant?: string;
    state?: string;
}

export interface CampaignsPageProps {}

export const CampaignsPage = ({}: CampaignsPageProps) => {
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const [searchParams, setSearchParams] = useSearchParams({} as CampaignsPageSearchParams);

    const { searchExpression } = useSearchInput(searchParams.get("search") ? searchParams.get("search") : undefined);

    const [state] = useState<CampaignState>(
        searchParams.get("state") ? (searchParams.get("state") as CampaignState) : undefined
    );

    useEffect(() => {
        const searchParams = {} as CampaignsPageSearchParams;

        if (searchExpression) {
            searchParams.search = searchExpression;
        }

        if (state?.length > 0) {
            searchParams.state = state;
        }

        setSearchParams(searchParams);
    }, [searchExpression, state, setSearchParams]);

    const campaignsHook = useCampaigns({
        client: client?.id,
        searchExpression,
        state,
    });

    const getRowHref = useCallback(
        (campaign: Campaign) => ({
            path: RoutePath.CAMPAIGN_DETAIL,
            params: { campaignId: campaign?.id },
        }),
        []
    );

    return (
        <>
            <Flex
                direction="column"
                style={{
                    height: "calc(100vh - (var(--mantine-header-height, 0px) + 2.5rem) - (var(--mantine-footer-height, 0px) + 2.5rem))",
                    maxHeight: "100%",
                    overflowY: "hidden",
                }}
            >
                <Flex
                    align="center"
                    justify="space-between"
                    mb="lg"
                    pb="md"
                    style={{
                        borderBottom: "1px solid #E0E0E0",
                    }}
                >
                    <Title order={3} style={{ fontWeight: 600 }}>
                        {t("campaigns")}
                    </Title>
                </Flex>
                <CampaignsDataGrid {...campaignsHook} getRowHref={getRowHref} />
                <Pagination {...campaignsHook.campaignsPagination} />
            </Flex>
        </>
    );
};
